import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Drawer, Flex, Popconfirm, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CrawlerEntity } from '../../../domain/entities/Crawler';
import { CrawlerChannelEntity, CrawlerChannelStatus, canCancelCrawlerChannel } from '../../../domain/entities/CrawlerChannel';
import { useCrawlerChannelsStore } from '../../stores/crawlerChannels.store';
import { useIsMobile } from '../../../services/responsiveUtils';
import AssignCrawlerChannelsModal from './AssignCrawlerChannelsModal.component';

interface CrawlerChannelsDrawerProps {
  crawler?: CrawlerEntity;
  onClose: () => void;
}

const STATUS_COLORS: Record<CrawlerChannelStatus, string> = {
  pending: 'processing',
  joined: 'cyan',
  request_pending: 'gold',
  listening: 'success',
  failed: 'error',
  unavailable: 'default',
};

const CrawlerChannelsDrawer: React.FC<CrawlerChannelsDrawerProps> = ({ crawler, onClose }) => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const isMobile = useIsMobile();
  const { items, isLoading, isSaving, error, load, assign, unassign, reset } = useCrawlerChannelsStore();
  const [isAssignOpen, setAssignOpen] = useState(false);

  const crawlerUUID = crawler?.uuid;
  useEffect(() => {
    if (crawlerUUID) load(crawlerUUID);
    else reset();
  }, [crawlerUUID, load, reset]);

  const dateFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }), [i18n.language]);

  const errorText = (slug: string) => t(slug, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) });

  const handleAssign = async (channelUuids: string[]) => {
    if (!crawler) return;
    try {
      await assign(crawler.uuid, channelUuids);
      message.success(t('crawler-channels-assigned', { ns: 'messages', count: channelUuids.length }));
      setAssignOpen(false);
    } catch (e) {
      message.error(errorText(e instanceof Error ? e.message : 'unknown-error'));
    }
  };

  const handleCancel = async (channel: CrawlerChannelEntity) => {
    if (!crawler) return;
    try {
      await unassign(crawler.uuid, channel.id);
      message.success(t('crawler-channel-unassigned', { ns: 'messages' }));
    } catch (e) {
      message.error(errorText(e instanceof Error ? e.message : 'unknown-error'));
    }
  };

  const columns: ColumnsType<CrawlerChannelEntity> = [
    {
      title: t('crawler-channel', { ns: 'labels' }),
      key: 'channel',
      render: (_, channel) => (
        <Flex vertical gap={2}>
          <Typography.Text strong>{channel.title || channel.link || channel.telegramChannelId}</Typography.Text>
          {channel.link && <Typography.Link href={channel.link} target="_blank" rel="noopener noreferrer">{channel.link}</Typography.Link>}
        </Flex>
      ),
    },
    {
      title: t('crawler-status', { ns: 'labels' }),
      key: 'status',
      render: (_, channel) => {
        const tag = <Tag color={STATUS_COLORS[channel.status]}>{t(`crawler-channel-status-${channel.status}`, { ns: 'labels' })}</Tag>;
        if (!channel.lastError) return tag;
        return (
          <Tooltip title={t('crawler-channel-error', { ns: 'labels', error: channel.lastError, count: channel.attempts })}>
            {tag}
          </Tooltip>
        );
      },
    },
    {
      title: t('crawler-channel-source', { ns: 'labels' }),
      key: 'source',
      render: (_, channel) => t(`crawler-channel-source-${channel.source}`, { ns: 'labels' }),
    },
    {
      title: t('crawler-channel-joined-at', { ns: 'labels' }),
      key: 'joinedAt',
      render: (_, channel) => (channel.joinedAt ? dateFormat.format(new Date(channel.joinedAt)) : '—'),
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, channel) => canCancelCrawlerChannel(channel) && (
        <Popconfirm
          title={t('crawler-channel-cancel-confirm', { ns: 'modals' })}
          okText={t('crawler-channel-cancel', { ns: 'buttons' })}
          cancelText={t('cancel', { ns: 'buttons' })}
          onConfirm={() => handleCancel(channel)}
        >
          <Button type="link" danger disabled={isSaving}>{t('crawler-channel-cancel', { ns: 'buttons' })}</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Drawer
      open={!!crawler}
      width={isMobile ? '100%' : 920}
      title={t('crawler-channels', { ns: 'titles', phone: crawler?.phone ?? '' })}
      onClose={onClose}
      destroyOnClose
      extra={(
        <Flex gap={8}>
          <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => crawler && load(crawler.uuid)} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignOpen(true)}>
            {t('crawler-assign-channels', { ns: 'buttons' })}
          </Button>
        </Flex>
      )}
    >
      <Typography.Paragraph type="secondary">{t('crawler-channels-hint', { ns: 'standard' })}</Typography.Paragraph>
      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={errorText(error)} />}
      <Table<CrawlerChannelEntity>
        rowKey={(channel) => (channel.id ? `a-${channel.id}` : `d-${channel.telegramChannelId}`)}
        columns={columns}
        dataSource={items}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 720 }}
        locale={{ emptyText: t('crawler-channels-empty', { ns: 'labels' }) }}
      />
      <AssignCrawlerChannelsModal
        open={isAssignOpen}
        isSaving={isSaving}
        onCancel={() => setAssignOpen(false)}
        onSubmit={handleAssign}
      />
    </Drawer>
  );
};

export default CrawlerChannelsDrawer;
